import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { SiteFooter } from "@/components/SiteFooter";
import { ArrowRight, HardHat, ShieldCheck, Linkedin, ClipboardCheck, Camera, Check } from "lucide-react";

const reveal = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
};
const panel = "rounded-2xl border border-white/5 bg-gradient-to-b from-[hsl(222,42%,15%)] to-[hsl(222,46%,10%)] shadow-xl shadow-black/30 ring-1 ring-white/5";

const TITLE = "About — built by a contractor, for contractors | Send-Proof";
const DESC = "How Send-Proof started: a contractor built it to verify work by photo instead of driving to each site. Text a punch list and check every item from your phone.";

const FOUNDER = { name: "Ryan Trexler", role: "Founder", photo: "/shots/founder.webp", linkedin: "https://share.google/SbJnKo3Il0dmdSbgm" };

export default function About() {
  const navigate = useNavigate();
  const go = () => navigate("/auth?mode=signup");

  useEffect(() => {
    const prevTitle = document.title;
    document.title = TITLE;
    const desc = document.querySelector('meta[name="description"]');
    const prevDesc = desc ? desc.getAttribute("content") : null;
    if (desc) desc.setAttribute("content", DESC);
    return () => {
      document.title = prevTitle;
      if (desc && prevDesc != null) desc.setAttribute("content", prevDesc);
    };
  }, []);

  return (
    <div className="relative min-h-[100dvh] overflow-hidden bg-card text-foreground">
      <div className="pointer-events-none absolute inset-x-0 top-0 z-0 h-[800px]">
        <div className="absolute left-1/2 top-[-200px] h-[500px] w-[820px] -translate-x-1/2 rounded-full bg-emerald-500/10 blur-[130px]" />
      </div>
      <div className="relative z-10">
        <header className="border-b border-white/10 bg-[hsl(222,46%,10%)]">
          <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4 sm:px-8">
          <Link to="/" className="flex items-center gap-2" data-testid="about-logo">
            <img src="/logo-mark.webp" alt="Send-Proof" className="h-8 w-8 rounded-lg" />
            <span className="font-heading text-lg font-bold tracking-tight">Send-Proof</span>
          </Link>
          <Button className="bg-emerald-500 hover:bg-emerald-600" data-testid="about-nav-cta" onClick={go}>Start free trial</Button>
          </div>
        </header>

        <motion.section variants={reveal} initial="hidden" animate="show" className="mx-auto max-w-2xl px-5 pt-10 text-center sm:px-8 sm:pt-16" data-testid="about-page">
          <span className="inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1 text-xs font-semibold text-emerald-500">
            <ShieldCheck className="h-3.5 w-3.5" /> Built by a contractor, for contractors
          </span>
          <h1 className="mt-5 font-heading text-4xl font-extrabold tracking-tight sm:text-5xl">Why we built Send-Proof</h1>
          <p className="mx-auto mt-4 max-w-lg text-base text-muted-foreground sm:text-lg" data-testid="about-definition">
            Send-Proof is a punch list app built by a contractor: you text your crew a link, get a photo on every item, and verify the work from your phone instead of driving to each site.
          </p>
        </motion.section>

        {/* Founder story */}
        <motion.section variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }} className="mx-auto max-w-2xl px-5 pt-10 sm:px-8">
          <div className={`${panel} p-7`} data-testid="about-founder">
            <div className="flex items-center gap-4">
              <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-full bg-emerald-500/15 text-emerald-400">
                {FOUNDER.photo ? <img src={FOUNDER.photo} alt={FOUNDER.name} className="h-full w-full object-cover" /> : <HardHat className="h-8 w-8" />}
              </div>
              <div className="min-w-0">
                <div className="font-heading text-lg font-bold">{FOUNDER.name}</div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  {FOUNDER.role || "Contractor & builder of Send-Proof"}
                  {FOUNDER.linkedin && <a href={FOUNDER.linkedin} target="_blank" rel="noopener noreferrer" data-testid="about-linkedin" className="text-muted-foreground hover:text-emerald-400"><Linkedin className="h-4 w-4" /></a>}
                </div>
              </div>
            </div>
            <div className="mt-5 space-y-4 text-base leading-relaxed text-foreground">
              <p>Every time a sub told me a job was done, the only way to be sure was to drive across town and check a box myself — burning gas and half a day just to confirm something I couldn't see from where I was.</p>
              <p>When you're running a bunch of projects at once, it's flat-out impossible to be at every site. I needed a way to verify the work without standing in front of it.</p>
              <p>So I built Send-Proof: text the crew a link, they photograph every item as they close it, and I verify the whole list right from my phone — from the next job, or the truck. A photo punch list gets things done way faster than a blue-tape walk, and I save the gas for the jobs that actually need me on site. That's it. That's Send-Proof.</p>
              <p className="text-sm text-muted-foreground">Send-Proof is built and operated by Arlo Douglas Labs LLC. Questions? Email <a href="mailto:support@send-proof.com" className="text-emerald-400 hover:text-emerald-300">support@send-proof.com</a> or visit our <Link to="/contact" className="text-emerald-400 hover:text-emerald-300">contact page</Link>.</p>
            </div>
          </div>
        </motion.section>

        {/* What it does */}
        <motion.section variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} className="mx-auto max-w-6xl px-5 pt-24 sm:px-8">
          <h2 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">What Send-Proof does</h2>
          <div className="mt-8 grid gap-5 sm:grid-cols-3">
            {[
              { icon: ClipboardCheck, t: "Make the list", d: "Build a punch list, add reference photos and notes, and text a link." },
              { icon: Camera, t: "They send proof back", d: "Your crew opens the link — no app — works each item and photographs it." },
              { icon: Check, t: "Verify it's done", d: "Get the ping, compare to your reference, tap to verify. Photo-backed record, no site visit." },
            ].map(({ icon: Icon, t, d }) => (
              <div key={t} className={`${panel} p-6`} data-testid={`about-step-${t}`}>
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-500 text-white"><Icon className="h-6 w-6" /></div>
                <h3 className="mt-4 font-heading text-base font-bold">{t}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{d}</p>
              </div>
            ))}
          </div>
        </motion.section>

        {/* CTA */}
        <motion.section variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} className="mx-auto max-w-2xl px-5 pb-24 pt-24 text-center sm:px-8">
          <div className="rounded-2xl border-2 border-emerald-500 bg-gradient-to-b from-[hsl(222,44%,14%)] to-[hsl(222,47%,9%)] p-8 shadow-2xl shadow-emerald-500/20">
            <h2 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">Try it on your next punch list</h2>
            <p className="mx-auto mt-2 max-w-md text-base text-muted-foreground">15 days free, no credit card. $9.99/month after — one flat plan.</p>
            <Button size="lg" className="mt-6 h-12 bg-emerald-500 px-8 text-base hover:bg-emerald-600" data-testid="about-cta" onClick={go}>
              Start 15-day free trial <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        </motion.section>

        <SiteFooter />
      </div>
    </div>
  );
}
